import { useState } from "react";
import { Loader2, Sparkles, Database } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PARTS } from "@/data/parts";
import { getBank, getAiItems, addAiItems, getSettings, saveSettings, resetBank } from "@/lib/bank";
import { loadVoices } from "@/lib/speech";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BankStatus = ({ bank, ai }) => (
  <div data-testid="data-bank-status" className="grid grid-cols-1 gap-2 sm:grid-cols-3">
    {PARTS.map((p) => (
      <div key={p.key} data-testid={`bank-count-${p.key}`} className="rounded-lg border border-slate-200 p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Part {p.key}</div>
        <div className="text-sm font-semibold text-slate-800">{p.name}</div>
        <div className="mt-1 font-mono text-xs"><span className={bank[p.key].length >= 100 ? "text-emerald-600" : "text-amber-600"}>{bank[p.key].length} loaded</span>
          {(ai[p.key] || []).length > 0 && <span className="text-indigo-600"> · {(ai[p.key] || []).length} AI</span>}</div>
      </div>
    ))}
  </div>
);

export const SettingsModal = ({ open, onOpenChange }) => {
  const { hasAccess } = useAuth();
  const [, force] = useState(0);
  const [settings, setSettings] = useState(getSettings());
  const [genPart, setGenPart] = useState("F");
  const [loading, setLoading] = useState(false);
  const bank = getBank(), ai = getAiItems();
  const total = Object.values(bank).reduce((s, a) => s + a.length, 0);
  const voices = loadVoices();

  const update = (patch) => { saveSettings(patch); setSettings(getSettings()); };
  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/generate", { part: genPart, count: 5 });
      const n = addAiItems(genPart, data.items);
      toast.success(`Added ${n} AI-written items to Part ${genPart}`);
      force((x) => x + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="settings-modal" className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Settings</DialogTitle>
          <DialogDescription>Question bank, voice and exam preferences.</DialogDescription>
        </DialogHeader>
        <section className="space-y-3">
          <div className="flex items-center justify-between"><h4 className="flex items-center gap-2 font-semibold text-slate-900"><Database className="h-4 w-4 text-indigo-600" />Data Bank Status</h4>
            <span data-testid="bank-total" className="font-mono text-xs text-slate-500">{total} items total</span></div>
          <BankStatus bank={bank} ai={ai} />
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-indigo-50 p-3">
            <span className="text-sm text-slate-700">Write 5 fresh items with AI for</span>
            <Select value={genPart} onValueChange={setGenPart}>
              <SelectTrigger data-testid="ai-generate-part-select" className="w-56 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{PARTS.map((p) => <SelectItem key={p.key} value={p.key}>Part {p.key} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
            <button data-testid="ai-generate-btn" onClick={hasAccess ? generate : () => toast.info("AI question writing is included with a Practice Pass.")} disabled={loading || !hasAccess} title={hasAccess ? "" : "Included with a Practice Pass"} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}Generate</button>
          </div>
          <button data-testid="reset-bank-btn" onClick={() => { resetBank(); force((x) => x + 1); toast.success("AI-written items removed"); }} className="text-xs font-semibold text-slate-500 underline hover:text-slate-800">Remove AI-written items</button>
        </section>
        <section className="space-y-3 border-t border-slate-200 pt-4">
          <h4 className="font-semibold text-slate-900">Exam length</h4>
          <div className="flex gap-2">
            {[["official", "Official (81 items)"], ["short", "Quick preview (1 per part)"]].map(([v, l]) => (
              <button key={v} data-testid={`exam-length-${v}`} onClick={() => update({ examLength: v })} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${settings.examLength === v ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{l}</button>
            ))}
          </div>
        </section>
        <section className="space-y-3 border-t border-slate-200 pt-4">
          <h4 className="font-semibold text-slate-900">Narrator voice</h4>
          {voices.length ? (
            <Select value={settings.voiceURI || voices[0].voiceURI} onValueChange={(v) => update({ voiceURI: v })}>
              <SelectTrigger data-testid="voice-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{voices.map((v) => <SelectItem key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</SelectItem>)}</SelectContent>
            </Select>
          ) : <p className="text-sm text-slate-500">No English voices detected in this browser yet.</p>}
        </section>
      </DialogContent>
    </Dialog>
  );
};
